import React, { useState, useEffect, useMemo } from "react";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing bootstrap components
import { Col, Card, Form, Button, Spinner } from "react-bootstrap";

import { useSelector, useDispatch } from "react-redux";

// User Query
import { setEmployees } from "state/slices/user/userSlice";

import { useGetUsersQuery } from "state/slices/user/userApiSlice";

// Import react-select
import Select from "react-select";

// Import utilities
import { valiDateOptionalFormField } from "validations";
import { showSuccessToast } from "utils/toast";

const initialRowValues = {
  employees: [],
  startTime: "",
  endTime: "",
  date: "",
};

const ManualAttendance = ({ markAttendance }) => {
  const [formikValues, setFormikValues] = useState(initialRowValues);
  const employees = useSelector((state) => state.employee.employees);

  const employeesArray = useMemo(() => {
    const allEmployeesOption = { employeeId: "all", name: "All Employees" };
    return [allEmployeesOption, ...employees].map((employee) => {
      return {
        value: employee.employeeId,
        label: employee.name,
      };
    });
  }, [employees]);

  const dispatch = useDispatch();

  // Retrieve Employee
  const { data: users } = useGetUsersQuery();

  useEffect(() => {
    if (users) {
      dispatch(setEmployees(users));
    }
  }, [users]);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const { endTime, ...otherValues } = values;

      const transferPackets = {
        ...otherValues,
        ...(valiDateOptionalFormField(endTime) ? { endTime } : {}),
      };

      // Check if attendance mark to all to employees
      let isMarkToAllEmployees = transferPackets.employees.some(
        (obj) => obj.value === "all"
      );

      // ? Changed to If 'All Employees' is selected
      const transferEmployeesArray = (
        isMarkToAllEmployees
          ? employeesArray.filter((obj) => obj.value !== "all")
          : transferPackets.employees
      ).map(({ value, label }) => ({ employeeId: value, name: label }));

      transferPackets.employees = transferEmployeesArray;
      
      if ( transferPackets.employees.length ) {

        await markAttendance(transferPackets).unwrap();
        showSuccessToast("Marking successful");
  
        // Reset form data
        resetForm();

      } 

    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  const validationSchema = Yup.object().shape({
    startTime: Yup.string().required("Attendance start time is required"),
    endTime: Yup.string().test(
      "endTime",
      "End time must be greater than start time",
      function (value) {
        const { startTime } = this.parent;
        if (startTime && value) {
          return startTime < value;
        }
        return true;
      }
    ),
    date: Yup.date().required("Attendance date is required"),
    day: Yup.string(),
    employees: Yup.array().required("Please select at least one employee"),
  });

  return (
    <Col md={12}>
      <Card className="mb-3">
        <Card.Body>
          <Formik
            enableReinitialize={true}
            initialValues={formikValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              handleChange,
              handleSubmit,
              touched,
              errors,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group controlId="employees">
                    <Select
                      isMulti
                      name="employees"
                      options={employeesArray}
                      value={values.employees}
                      onChange={(selectedOptions) => {
                        setFieldValue("employees", selectedOptions);
                      }}
                      isInvalid={touched.employees && !!errors.employees}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.employees}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3 mb-md-0">
                  <Form.Group controlId="startTime">
                    <Form.Control
                      type="time"
                      name="startTime"
                      value={values.startTime}
                      onChange={handleChange}
                      isInvalid={touched.startTime && !!errors.startTime}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.startTime}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2} className="mb-3 mb-md-0">
                  <Form.Group controlId="endTime">
                    <Form.Control
                      type="time"
                      name="endTime"
                      value={values.endTime}
                      onChange={handleChange}
                      isInvalid={touched.endTime && !!errors.endTime}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.endTime}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group controlId="date">
                    <Form.Control
                      type="date"
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                      isInvalid={touched.date && !!errors.date}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.date}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button variant="primary" className="w-100" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                          style={{ width: "18px", height: "18px" }}
                        />
                        Marking...
                      </>
                    ) : (
                      "Mark"
                    )}
                  </Button>
                </Col>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default ManualAttendance;
